import { useCallback, useMemo, useState } from 'react'

import { useUpdateChatMessageMutation } from '@crew/apis/chat/chatApis'
import {
  useChatCurrentService,
  useChatMessage,
  useChatThreadService,
  useChatTimelineService,
} from '@crew/states'

import { toggleRightSideBar } from 'features/app/states/appSlice'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { RelatedEntity } from '@crew/models/common'

export const useChatTimelineMessageListItem = (
  chatMessageId: string,
  topicId: string
) => {
  // 処理対象のチャットルームをViewModelから取得
  const currentChatRoom = useAppSelector(
    (state) => state.message.chat.current.chatRoom
  )
  // この処理が流れる際、ViewModelには必ずチャットルームが設定されているはずなので、未設定の場合はエラーとする
  if (!currentChatRoom) {
    throw new Error('currentChatRoom is undefined')
  }

  const dispatch = useAppDispatch()
  const { message, isError } = useChatMessage(
    chatMessageId,
    dispatch,
    useAppSelector
  )

  // 編集モードかどうか
  const [isEditMode, setIsEditMode] = useState(false)

  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

  // 右パネルが開いているかどうか
  // 返信ボタンクリック時に、右パネルが開いていなければ開くために使用
  const rightSideBarOpened = useAppSelector(
    (state) => state.app.rightSideBarOpened
  )

  const [updateChatMessageMutation] = useUpdateChatMessageMutation()

  // 自分が投稿したメッセージ判定（自分のメッセージのみ編集可能とするフラグ）
  const isMyMessage = useMemo(
    () => message?.createdById === loggedInUser?.id,
    [loggedInUser?.id, message?.createdById]
  )

  // Sliceの操作を行うためのServiceを取得
  const chatTimelineService = useChatTimelineService(dispatch)
  const chatThreadService = useChatThreadService(dispatch)
  const chatCurrentService = useChatCurrentService(dispatch)

  // 返信のクリックイベントハンドラ
  const openTargetThread = useCallback(() => {
    // メッセージがクリックされたときにスレッド表示に遷移するためtopicIdをセットする
    chatTimelineService.setTimelineTopicId({
      chatRoomId: currentChatRoom.id,
      chatMessageId: topicId,
    })

    if (topicId !== chatMessageId) {
      // トピックメッセージ以外を選択した場合、選択メッセージIDにセット
      chatThreadService.setChatThreadSelectedMessageId({
        topicId,
        chatMessageId,
      })
    }

    // トピックが存在する場合、現在のチャットスレッドを更新する
    if (message) {
      chatCurrentService.setCurrentChatThread({
        topicId: topicId,
        chatRoomId: message.chatRoomId,
        rootEntityType: message.chatRoom.rootEntityType,
        rootEntityRecordId: message.chatRoom.rootEntityRecordId,
        entityType: message.chatRoom.entityType,
        entityRecordId: message.chatRoom.entityRecordId,
      })
    }

    // 右サイドバーを開く
    !rightSideBarOpened && dispatch(toggleRightSideBar())
  }, [
    chatTimelineService,
    currentChatRoom.id,
    topicId,
    chatMessageId,
    message,
    rightSideBarOpened,
    dispatch,
    chatThreadService,
    chatCurrentService,
  ])

  // 「編集」押下時に編集モードを切り替える
  const toggleEditMode = useCallback(() => {
    setIsEditMode(!isEditMode)
  }, [isEditMode])
  // 編集モードの登録 / キャンセル時に編集モードをoffにする
  const cancelEditMode = useCallback(() => {
    setIsEditMode(false)
  }, [])

  // 添付ファイル削除後にメッセージ更新APIを実行してwebsocketによる表示の更新を行う
  const updateChatMessage = useCallback(async () => {
    if (!message) {
      return
    }

    // メンション
    const mentionUserIds: string[] = message.mentions
      ? Object.values(message.mentions).map((item) => {
          return item.userId
        })
      : []

    // 関連エンティティ
    const relatedEntities: RelatedEntity[] = message.entityReferences
      ? Object.values(message.entityReferences).map((item) => {
          return {
            entityType: item.sourceEntityType,
            entityRecordId: item.sourceEntityRecordId,
          }
        })
      : []

    try {
      await updateChatMessageMutation({
        chatMessage: {
          chatMessageId: message?.id,
          text: message?.text,
          version: message?.version,
        },
        mentionUserIds:
          mentionUserIds.length === 0 ? undefined : mentionUserIds,
        relatedTargets:
          relatedEntities.length === 0 ? undefined : relatedEntities,
      })
    } catch (err) {
      // 成功可否によるトースト表示などはないのでエラー時はエラーログを出力
      console.error(err)
    }
  }, [message, updateChatMessageMutation])

  return {
    message,
    isError,
    toggleEditMode,
    cancelEditMode,
    openTargetThread,
    updateChatMessage,
    isMyMessage,
    isEditMode,
  }
}
